import {
  DndProviderControlProps,
  ImageMarkerBox,
  ImageMarkerType,
} from '@/components';
import {
  BaselinePersonalizationItemState,
  BaselineSpecialRequestState,
  useGetBaselinePersonalizationItemsQuery,
  useGetBaselineSpecialRequestsQuery,
  useGetProjectHomeInfoQuery,
} from '@/redux';
import { array } from '@/utils';
import { useFlippoBlobUrl } from '@/utils/flippo';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaselineImageSelectControl } from './BaselineImageSelectControl';

interface Props extends DndProviderControlProps {
  homeId: string;
  initialProjectId: string;
  floorplanId: string;
  projectIds?: string[] | null;
  className?: string;
  innerRef?: React.Ref<HTMLDivElement>;
}

function useMarkers(props: Props) {
  const { homeId, projectIds } = props;
  const { t } = useTranslation(undefined, {
    keyPrefix: 'personalization.floorplan.geo',
  });
  const { data: items = array.empty<BaselinePersonalizationItemState>() } =
    useGetBaselinePersonalizationItemsQuery({
      homeId,
      projectIds: projectIds ?? undefined,
      isGeo: true,
      isFloorplan: true,
    });
  const { data: specialRequests = array.empty<BaselineSpecialRequestState>() } =
    useGetBaselineSpecialRequestsQuery({
      homeId,
      projectIds: projectIds ?? undefined,
      isFloorplan: true,
    });

  const markers = useMemo(() => {
    const itemsData = items.map((item) => ({
      ...item.position!,
      number: item.number!,
      label: `${item.project.name} - ${item.category.root.name} - ${item.name}`,
      specialRequest: undefined,
      item: item,
      type: 'circle' as ImageMarkerType,
      hexColor: item.hexColor ?? undefined,
    }));

    const specialRequestsData = specialRequests
      .filter((x) => x.status !== 'Denied' && x.status !== 'ResidentDeclined')
      .map((item) => ({
        ...item.position!,
        number: item.number!,
        label:
          item.project.name +
          '-' +
          t('specialRequestName', {
            name: item.name,
          }),
        specialRequest: item,
        item: undefined,
        type: 'star' as ImageMarkerType,
        hexColor: undefined,
      }));

    return [...itemsData, ...specialRequestsData].sort(
      (a, b) => a.number - b.number,
    );
  }, [items, specialRequests, t]);

  return { markers };
}

function useFloorplan(props: Props) {
  const { homeId, initialProjectId, floorplanId } = props;
  const { data: lastProjectHome } = useGetProjectHomeInfoQuery({
    homeId,
    id: initialProjectId,
  });

  const { url: floorplanImageUrl } = useFlippoBlobUrl(
    lastProjectHome?.floorplanImageReference,
  );

  const [imageUrl, setImageUrl] = useState<string>();

  useEffect(() => {
    floorplanImageUrl && setImageUrl(floorplanImageUrl);
  }, [floorplanImageUrl]);

  return {
    floorplanId: floorplanId,
    floorplanImageUrl: imageUrl,
    setImageUrl,
  };
}

function _BaselineFloorplanPanel(props: Props) {
  const { homeId, className, innerRef, skipProvider } = props;
  const { markers } = useMarkers(props);
  const { floorplanId, floorplanImageUrl, setImageUrl } = useFloorplan(props);

  return (
    <ImageMarkerBox
      innerRef={innerRef}
      markers={markers}
      onMarkersChange={() => {}}
      src={floorplanImageUrl}
      readonly
      actions={
        <BaselineImageSelectControl
          homeId={homeId}
          floorplanId={floorplanId}
          setImageUrl={setImageUrl}
        />
      }
      className={className}
      skipProvider={skipProvider}
    />
  );
}

export function BaselineFloorplanPanel(props: Props) {
  return <_BaselineFloorplanPanel {...props} key={'baseline' + props.homeId} />;
}
